/* eslint-disable import/prefer-default-export -- hook may also be default-exported */
import { useCallback } from "react";

import { useAuth, type Achievement } from "./auth";
import {
  ACTION_POINTS,
  gamificationService,
  type GamificationAction,
} from "../services/gamificationService";
import { ACHIEVEMENTS } from "../config/achievements";

export function useGamification() {
  const { user, updateUserProfile } = useAuth();

  const addPoints = useCallback(
    async (action: GamificationAction) => {
      if (!user?.uid) {
        return { newAchievements: [] as Achievement[], pointsGained: 0 };
      }
      try {
        return await gamificationService.addPoints(
          user,
          action,
          updateUserProfile
        );
      } catch {
        return { newAchievements: [] as Achievement[], pointsGained: 0 };
      }
    },
    [user, updateUserProfile]
  );

  const getLevelProgress = useCallback(() => {
    const points = user?.points || 0;
    return {
      currentLevel: user?.level || gamificationService.calculateLevel(points),
      pointsToNext: gamificationService.getPointsToNextLevel(points),
      progress: gamificationService.getLevelProgressPercent(points),
      points,
    };
  }, [user]);

  const getAchievements = useCallback(() => user?.achievements || [], [user]);

  const getAllAchievementsWithStatus = useCallback(() => {
    const unlockedIds = new Set((user?.achievements || []).map((a) => a.id));
    return Object.values(ACHIEVEMENTS).map((def) => ({
      ...def,
      unlocked: unlockedIds.has(def.id),
      unlockedAt: user?.achievements?.find((a) => a.id === def.id)?.unlockedAt,
    }));
  }, [user]);

  const getStats = useCallback(() => {
    if (!user?.uid) return null;
    return {
      points: user.points || 0,
      level: user.level || 1,
      streakDays: user.streakDays || 0,
      totalWordsViewed: user.totalWordsViewed || 0,
      totalSearches: user.totalSearches || 0,
      totalFavorites: (user as { totalFavorites?: number }).totalFavorites || 0,
      achievementsCount: user.achievements?.length || 0,
    };
  }, [user]);

  return {
    addPoints,
    getLevelProgress,
    getAchievements,
    getAllAchievementsWithStatus,
    getStats,
    ACTION_POINTS,
    user,
  };
}

export default useGamification;
